import { BillingHistory } from '@/components/subscription/BillingHistory';
import { BillingManagement } from '@/components/subscription/BillingManagement';
import { CheckoutDialog } from '@/components/subscription/CheckoutDialog';
import { DangerZone } from '@/components/subscription/DangerZone';
import { SubscriptionSettings } from '@/components/subscription/SubscriptionSettings';
import { TierSelector } from '@/components/subscription/TierSelector';
import { Button } from '@/components/ui/button';
import { useOrganization, useOrganizations } from '@/hooks/useOrganizations';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useSubscription } from '@/hooks/useSubscription';
import PageSkeleton from '@/layout/Skeleton';
import dayjs from 'dayjs';
import { AlertTriangle, CreditCard, Loader2, Sparkles, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

export default function SubscriptionPage() {
  const organization = useOrganization();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasRole } = useOrganizations();
  const { t } = useTranslation('subscription');
  const { subscription, features, fetchSubscription, reactivateSubscription, initialLoadComplete, loading } =
    useSubscription();
  usePageMeta({ title: [t('page.breadcrumb')] });
  const needsSubscription =
    features.hasNoSubscription || features.isCanceled || subscription.billingStatus === 'UNPAID';
  const [showCheckout, setShowCheckout] = useState(false);

  const handleCheckoutRedirect = useCallback(() => {
    const checkoutStatus = searchParams.get('checkout');

    if (checkoutStatus === 'success') {
      toast.success(t('page.checkoutSuccess'));
      searchParams.delete('checkout');
      setSearchParams(searchParams, { replace: true });
      fetchSubscription();
    } else if (checkoutStatus === 'canceled') {
      toast.info(t('page.checkoutCanceled'));
      searchParams.delete('checkout');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, fetchSubscription]);

  useEffect(() => {
    handleCheckoutRedirect();
  }, [handleCheckoutRedirect]);

  if (!organization) {
    return <div>{t('page.orgNotFound')}</div>;
  }

  if (!hasRole('BILLING_MANAGER')) {
    return (
      <PageSkeleton steps={[{ label: t('page.breadcrumb'), href: '/subscription' }]}>
        <div className='max-w-2xl mx-auto px-4 pt-6 w-full'>
          <div className='text-center py-12'>
            <h2 className='text-xl font-semibold text-slate-900 dark:text-white mb-2'>{t('page.accessRestricted')}</h2>
            <p className='text-slate-500 dark:text-stone-400'>{t('page.accessRestrictedDescription')}</p>
          </div>
        </div>
      </PageSkeleton>
    );
  }

  return (
    <PageSkeleton steps={[{ label: t('page.breadcrumb'), href: '/subscription' }]}>
      <div className='max-w-2xl mx-auto px-4 pt-6 w-full space-y-6 pb-12 overflow-y-auto'>
        {/* Trial banner */}
        {features.isTrialing && features.trialEndsAt && (
          <div className='rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white'>
            <div className='flex items-start gap-3'>
              <Sparkles className='size-6 shrink-0 mt-0.5' />
              <div className='flex-1'>
                <h2 className='text-lg font-semibold'>{t('trial.title', { tier: subscription.plan })}</h2>
                <p className='text-sm text-blue-100 mt-1'>
                  {t('trial.description', { days: Math.max(0, dayjs(features.trialEndsAt).diff(dayjs(), 'day')) })}
                </p>
                <div className='flex items-center gap-4 mt-4'>
                  <Button variant='secondary' size='sm' onClick={() => setShowCheckout(true)} className='gap-1.5'>
                    <CreditCard className='size-4' />
                    {t('trial.subscribeNow')}
                  </Button>
                  <span className='text-xs text-blue-200'>
                    {t('trial.trialEnds', { date: dayjs(features.trialEndsAt).format('MMMM D, YYYY') })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Alert banners */}
        {features.isCancelScheduled && (
          <div className='flex items-start gap-3 rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950/20 p-4'>
            <AlertTriangle className='h-5 w-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5' />
            <div className='flex-1 min-w-0'>
              <p className='text-sm font-medium text-yellow-800 dark:text-yellow-200'>
                {t('alerts.cancellationScheduled')}
              </p>
              <p className='text-sm text-yellow-700 dark:text-yellow-300 mt-0.5'>
                {t('alerts.cancellationScheduledDescription', {
                  date: features.cancelAt
                    ? dayjs(features.cancelAt).format('MMM D, YYYY')
                    : t('alerts.cancellationScheduledDescriptionFallback'),
                })}
              </p>
            </div>
            <Button
              variant='outline'
              size='sm'
              disabled={loading}
              className='shrink-0 border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900/30'
              onClick={async () => {
                try {
                  await reactivateSubscription();
                  toast.success(t('alerts.reactivatedSuccess'));
                } catch {
                  toast.error(t('alerts.reactivateFailed'));
                }
              }}
            >
              {loading ? t('alerts.reactivating') : t('alerts.reactivate')}
            </Button>
          </div>
        )}

        {features.isPastDue && (
          <div className='flex items-start gap-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-4'>
            <XCircle className='h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5' />
            <div>
              <p className='text-sm font-medium text-red-800 dark:text-red-200'>{t('alerts.paymentPastDue')}</p>
              <p className='text-sm text-red-700 dark:text-red-300 mt-0.5'>{t('alerts.paymentPastDueDescription')}</p>
            </div>
          </div>
        )}

        {/* Overview */}
        <SubscriptionSettings />

        {/* Upgrade banner for users without an active subscription */}
        {!initialLoadComplete ? (
          <div className='flex items-center justify-center py-8'>
            <Loader2 className='size-5 animate-spin text-slate-400' />
          </div>
        ) : needsSubscription ? (
          <div className='rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white'>
            <div className='flex items-start gap-3'>
              <CreditCard className='size-6 shrink-0 mt-0.5' />
              <div className='flex-1'>
                <h2 className='text-lg font-semibold'>
                  {features.isCanceled ? t('upgradeBanner.subscriptionEnded') : t('upgradeBanner.subscribeToPlan')}
                </h2>
                <p className='text-sm text-blue-100 mt-1'>
                  {features.isCanceled ? t('upgradeBanner.canceledDescription') : t('upgradeBanner.freeDescription')}
                </p>
                <Button variant='secondary' size='sm' className='mt-4 gap-1.5' onClick={() => setShowCheckout(true)}>
                  <CreditCard className='size-4' />
                  {features.isCanceled ? t('upgradeBanner.resubscribe') : t('upgradeBanner.viewPlans')}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Plan (active paid subscribers only — not during trial).
            Storage is shown in the overview (SubscriptionSettings), not as a
            separate section. */}
        {!needsSubscription && features.isActive && <TierSelector />}

        {/* Billing (active paid subscribers only) */}
        {!needsSubscription && features.isActive && (
          <>
            <BillingManagement />
            <BillingHistory />
          </>
        )}

        {/* Danger Zone (not during trial) */}
        {!features.isTrialing && <DangerZone />}

        {showCheckout && <CheckoutDialog open={showCheckout} onOpenChange={setShowCheckout} />}
      </div>
    </PageSkeleton>
  );
}
